class HHMobileMenu extends HTMLElement {
  constructor() {
    super()

    this.submenus = this.querySelectorAll('details')
    this.submenus.forEach((submenu) => {
      const summary = submenu.querySelector(':scope > summary')
      summary?.addEventListener('click', this.onSummaryClick.bind(this))
    })

    this.onWindowResize = this.setWindowHeightVariable.bind(this)
    this.onModalClosed = this.onMobileMenuClose.bind(this)
    this.onModalOpened = this.onMobileMenuOpen.bind(this)
  }

  connectedCallback() {
    this.setWindowHeightVariable()
    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('modalOpened', this.onModalOpened)
    window.addEventListener('modalClosed', this.onModalClosed)
    this.initAutoExpanded()
  }

  initAutoExpanded() {
    this.submenus.forEach((submenu) => {
      if (!submenu.hasAttribute('data-auto-expand')) return

      submenu.setAttribute('open', '')
      submenu.classList.add('is-open')
      const content = submenu.querySelector(':scope > .accordion-content')
      if (content) content.style.maxHeight = 'none'
    })

    const anyOpen = Array.from(this.submenus).some((submenu) =>
      submenu.classList.contains('is-open')
    )
    this.toggleAttribute('submenu-open', anyOpen)
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('modalOpened', this.onModalOpened)
    window.removeEventListener('modalClosed', this.onModalClosed)
  }

  setWindowHeightVariable() {
    HH.setCSSVariable('--hh-window-height', `${window.innerHeight}px`)
  }

  // Drives the accordion open/close ourselves instead of the native
  // <details> toggle - see the comment on .accordion-content in
  // snippet-mobile-menu.css for why: browsers hide a closed <details>'s
  // content through their own internal mechanism, which only leaves room
  // for our CSS transition to play once (the first open) before it starts
  // winning on every later toggle. Preventing the native toggle and keeping
  // [open] permanently set from here on means that mechanism never
  // re-engages; ".is-open" (toggled below) drives the animation instead.
  onSummaryClick(e) {
    e.preventDefault()

    const details = e.currentTarget.closest('details')
    if (!details) return

    const isOpening = !details.classList.contains('is-open')

    if (isOpening) {
      this.closeSiblings(details)
    }

    details.classList.toggle('is-open', isOpening)
    if (!details.hasAttribute('open')) {
      details.setAttribute('open', '')
    }

    this.setAccordionHeight(details, isOpening)
    if (isOpening) this.revealSummary(details)

    const anyOpen = Array.from(this.submenus).some((submenu) =>
      submenu.classList.contains('is-open')
    )
    this.toggleAttribute('submenu-open', anyOpen)
  }

  // Closing an open sibling above shrinks the list, so the summary just tapped slides up out of view
  // and the user lands part-way down the new submenu. Once the accordions have settled, bring it back
  // to the top of the scroll area, as the PDP accordions do (ls-pdp-accordion.js).
  revealSummary(details) {
    const scrollContainer = this.querySelector('.js-mobile-menu-scroll')
    const summary = details.querySelector(':scope > summary')
    if (!scrollContainer || !summary) return

    setTimeout(() => {
      const offset = summary.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top
      if (offset < 0) scrollContainer.scrollBy({ top: offset, behavior: 'smooth' })
    }, 300)
  }

  closeSiblings(details) {
    const parentList = details.parentElement?.closest('ul')
    if (!parentList) return

    parentList.querySelectorAll(':scope > li > details.is-open').forEach((sibling) => {
      if (sibling === details) return
      sibling.classList.remove('is-open')
      this.setAccordionHeight(sibling, false)
    })
  }

  // JS-measured max-height animation, not the CSS-only grid-template-rows
  // 0fr/1fr trick this used to use: that trick doesn't reliably collapse to
  // a true 0 in every browser once one accordion is nested inside another
  // (interpolating a "fr" track through an ancestor that's also animating
  // its own "fr" track is exactly what was leaving part of a closed
  // accordion's content permanently visible here). Measuring real pixel
  // heights instead always collapses fully, regardless of nesting depth.
  setAccordionHeight(details, isOpening) {
    const content = details.querySelector(':scope > .accordion-content')
    if (!content) return

    if (isOpening) {
      content.style.maxHeight = `${content.scrollHeight}px`
    } else {
      // Give it a definite starting height first - it may currently be
      // "none" (see the transitionend handler below), which can't animate
      // from - then force a reflow so the browser registers that before
      // the change to 0 that should actually transition.
      content.style.maxHeight = `${content.scrollHeight}px`
      // eslint-disable-next-line no-unused-expressions
      content.offsetHeight
      content.style.maxHeight = '0px'
    }

    // Once fully open, let this content size itself normally (rather than
    // staying pinned to the px height measured at open time) so anything
    // that changes its height later - a nested accordion opening inside
    // it, a late-loading image - isn't clipped. setAccordionHeight() sets
    // a concrete height again first thing on the next close, so this is
    // safe to leave in place until then.
    content.addEventListener(
      'transitionend',
      () => {
        if (details.classList.contains('is-open')) {
          content.style.maxHeight = 'none'
        }
      },
      { once: true }
    )

    this.resizeOpenAncestors(details)
  }

  // A nested accordion changing height needs any open ancestor
  // accordion's own max-height (a concrete px snapshot, not "none",
  // whenever it's mid- or post-transition) recalculated to match -
  // otherwise it either clips the new content or leaves dead space.
  resizeOpenAncestors(details) {
    let ancestorDetails = details.parentElement?.closest('details')

    while (ancestorDetails) {
      if (ancestorDetails.classList.contains('is-open')) {
        const ancestorContent = ancestorDetails.querySelector(':scope > .accordion-content')
        if (ancestorContent && ancestorContent.style.maxHeight !== 'none') {
          ancestorContent.style.maxHeight = `${ancestorContent.scrollHeight}px`
        }
      }
      ancestorDetails = ancestorDetails.parentElement?.closest('details')
    }
  }

  onMobileMenuOpen(e) {
    const { target } = e
    if (!target.classList?.contains('js-mobile-menu')) return

    // The scrollable area is now just the accordion nav (see
    // .js-mobile-menu-scroll in mobile-menu.liquid) - the outer
    // .js-mobile-menu-container is a non-scrolling flex column so the
    // footer can stay pinned to the bottom of the drawer.
    const scrollContainer = target.querySelector('.js-mobile-menu-scroll')
    const innerScrollContainers = scrollContainer?.querySelectorAll('.js-inner-submenu')

    const resetScroll = () => {
      scrollContainer?.scrollTo({ top: 0, behavior: 'auto' })
      innerScrollContainers?.forEach((innerScrollContainer) => {
        innerScrollContainer.scrollTo({ top: 0, behavior: 'auto' })
      })
    }

    resetScroll()
    window.requestAnimationFrame(resetScroll)
  }

  onMobileMenuClose(e) {
    const { target } = e
    if (!target.classList.contains('js-mobile-menu')) return
    this.submenus.forEach((submenu) => {
      submenu.removeAttribute('open')
      submenu.classList.remove('is-open')
      const content = submenu.querySelector(':scope > .accordion-content')
      if (content) content.style.maxHeight = ''
    })
    this.removeAttribute('submenu-open')
    this.initAutoExpanded()
  }
}

customElements.define('hh-mobile-menu', HHMobileMenu)
